// Таймлайн окон пролёта выбранного спутника над промысловым районом (раздел
// «Спутники»). Свой useQuery к /passes на окно 24 ч от эффективного момента:
// from = выбранный момент (или «сейчас»), to = from + 24 ч. Ключ включает NORAD и
// момент; в живом режиме автообновляется в темпе liveRefetch. Панель схлопываемая,
// стоит слева от карточки спутника, чтобы не перекрывать её. Стиль — Румб, тема.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { useT } from '../i18n'
import { humanDate } from '../models/driftDay'
import { formatPassWindow, groupPassesByDay } from './coverage'

interface PassesPanelProps {
  /** NORAD выбранного спутника (панель рендерится только при выборе). */
  norad: string
  /** Эффективный момент ISO (undefined — живой режим «сейчас»). */
  apiTime: string | undefined
  /** Период автообновления (60_000 в живом режиме, false при фиксированном моменте). */
  liveRefetch: number | false
}

const DAY_MS = 24 * 60 * 60 * 1000

/** ISO-день (YYYY-MM-DD) → «30 июля» / «30 July». День и месяц без года: панель
 *  показывает ближайшие сутки, год в подписи был бы шумом. */
function dayLabel(day: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day
  return humanDate(day)
}

export function PassesPanel({ norad, apiTime, liveRefetch }: PassesPanelProps) {
  const t = useT()
  const [open, setOpen] = useState(true)

  // Верхняя граница окна: эффективный момент (или «сейчас») + 24 ч.
  const to = useMemo(() => {
    const base = apiTime ? new Date(apiTime) : new Date()
    return new Date(base.getTime() + DAY_MS).toISOString()
  }, [apiTime])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cabinet', 'satellites', 'passes', norad, apiTime ?? 'now'],
    queryFn: () => cabinetApi.satellites.passes(norad, { from: apiTime, to }),
    enabled: !!norad,
    refetchInterval: liveRefetch,
  })

  const groups = useMemo(() => groupPassesByDay(data?.passes ?? []), [data])
  const count = data?.count ?? 0

  return (
    <div className="console-scroll pointer-events-auto absolute bottom-4 right-[19.5rem] z-10 max-h-[calc(100%-2rem)] w-72 overflow-y-auto rounded-xl border border-hull bg-abyss/85 p-3 shadow-console backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-2 text-left"
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foam">{t('satellites.passes.title')}</div>
          <div className="mt-0.5 text-[11px] text-mist">
            {t('satellites.passes.area')}
            {count > 0 ? ` · ${count}` : ''}
          </div>
        </div>
        <svg
          viewBox="0 0 12 12"
          className={[
            'mt-0.5 h-3 w-3 shrink-0 text-mist transition-transform',
            open ? '' : '-rotate-90',
          ].join(' ')}
          aria-hidden
        >
          <path
            d="M2 4l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="mt-2 border-t border-hull-hair pt-2">
          {isLoading && <div className="text-xs text-mist">{t('satellites.passes.computing')}</div>}

          {isError && (
            <div className="text-xs text-mist">{t('satellites.passes.failed')}</div>
          )}

          {!isLoading && !isError && count === 0 && (
            <div className="text-xs text-mist">
              {t('satellites.passes.none')}
            </div>
          )}

          {!isLoading && !isError && count > 0 && (
            <div className="space-y-2.5">
              {groups.map(g => (
                <div key={g.day}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-faint">
                    {dayLabel(g.day)}
                  </div>
                  <ul className="mt-1 space-y-1">
                    {g.passes.map((p, i) => {
                      const w = formatPassWindow(p)
                      return (
                        <li
                          key={`${p.start}-${i}`}
                          className="flex items-baseline justify-between gap-2 text-[12px]"
                        >
                          <span className="tnum text-foam">
                            {w.startHM}–{w.endHM}
                          </span>
                          <span className="tnum shrink-0 text-right text-mist">
                            {w.durationLabel} · {t('satellites.km', { value: Math.round(p.min_distance_km) })}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
